import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import { useReviewStore } from '../stores/reviewStore'
import BoldCheckbox from '../components/BoldCheckbox'
import toastUtil from '../utils/toast'
import { getPriceRangeOptions, PriceRangeId } from '../constants/priceRange'

const PriceRangeAdd: React.FC = () => {
  const navigate = useNavigate()
  const {
    minPricePerPerson: storeMinPrice,
    maxPricePerPerson: storeMaxPrice,
    setPricePerPerson,
    storeInfo,
  } = useReviewStore()

  /*
   * 카페는 눈금이 다르다.
   *
   * 기본 구간(15,000 / 30,000 / 70,000)으로는 카페 기록의 75%가 첫 칸에
   * 몰려 커피 한 잔과 디저트 세트가 같은 ₩ 하나로 보였다.
   */
  const options = getPriceRangeOptions(
    storeInfo?.categoryCode ?? storeInfo?.category ?? null
  )

  // 현재 선택된 옵션 찾기
  const findSelectedOption = (): PriceRangeId | null => {
    if (storeMinPrice === null && storeMaxPrice === null) {
      return null
    }
    /*
     * 구간을 바꾸기 전에 찍힌 카페 기록은 지금 선택지와 맞아떨어지지 않는다.
     * 그때는 아무것도 고르지 않은 상태로 둔다 — 새 눈금으로 다시 골라야
     * 맞는 값이 되고, 임의로 하나를 집어 주면 고르지도 않은 값이 저장된다.
     */
    const selected = options.find(
      (option) => option.min === storeMinPrice && option.max === storeMaxPrice
    )
    return selected ? selected.id : null
  }

  const [selectedOptionId, setSelectedOptionId] = useState<PriceRangeId | null>(
    () => findSelectedOption()
  )

  const handleOptionToggle = (optionId: PriceRangeId) => {
    if (selectedOptionId === optionId) {
      // 이미 선택된 옵션을 다시 클릭하면 선택 해제
      setSelectedOptionId(null)
    } else {
      // 새로운 옵션 선택
      setSelectedOptionId(optionId)
    }
  }

  const handleSubmit = () => {
    if (!selectedOptionId) {
      toastUtil.warning('1인당 가격대를 선택해주세요')
      return
    }

    // 선택된 옵션을 스토어에 저장
    const selectedOption = options.find((opt) => opt.id === selectedOptionId)
    if (selectedOption) {
      setPricePerPerson(selectedOption.min, selectedOption.max)
    }

    // 완료 후 뒤로가기
    navigate(-1)
  }

  const handleBack = () => {
    // 뒤로가기 (저장하지 않음)
    navigate(-1)
  }

  const isFormValid = selectedOptionId !== null

  const renderPriceRangeOptions = () => {
    return (
      <div className="mt-16px w-full flex flex-col">
        {options.map((option) => {
          const isSelected = selectedOptionId === option.id
          return (
            <div
              key={option.id}
              className={`
                py-12px
                w-full
                flex items-center gap-12px
                cursor-pointer
                hover:opacity-70
                ${isSelected ? '' : ''}
              `}
              onClick={() => handleOptionToggle(option.id)}
            >
              {/* 체크박스 */}
              <div onClick={(e) => e.stopPropagation()}>
                <BoldCheckbox
                  size={24}
                  checked={isSelected}
                  onChange={(checked) => {
                    if (checked) {
                      setSelectedOptionId(option.id)
                    } else {
                      setSelectedOptionId(null)
                    }
                  }}
                />
              </div>

              {/* 아이콘 */}
              <span className="flex-1 font-pretendard font-regular text-16px leading-20px text-brown-80%-main text-left">
                {option.icon}
              </span>

              {/* 라벨 */}
              <span className="font-pretendard font-regular text-16px leading-20px text-textColorText3 text-right">
                {option.label}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  const renderSubmitButton = () => {
    return (
      <div className="w-full px-20px">
        <SubmitButton
          text="완료"
          onSubmit={handleSubmit}
          disabled={!isFormValid}
          onDisabled={handleSubmit}
          addClassName="mt-20px"
        />
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <div className="app-container flex-1 flex flex-col overflow-hidden pb-32px">
        {/* AppBar */}
        <AppBar title="1인당 가격대 기록" isBottomBorder onBack={handleBack} />

        {/* 컨텐츠 영역 */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-20px pb-24px">
          {/* 가격대 옵션 */}
          {renderPriceRangeOptions()}
        </div>

        {/* 하단 버튼 */}
        {renderSubmitButton()}
      </div>
    </div>
  )
}

export default PriceRangeAdd
